import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, ArrowUp, ArrowDown, Pencil, Archive, ArchiveRestore, Mail, Eye, Download, Wand2 } from "lucide-react";
import { EmailTemplateDialog } from "@/components/marketing/EmailTemplateDialog";
import { PersonalizeDialog } from "@/components/marketing/PersonalizeDialog";
import { PngDownloadLink } from "@/components/marketing/PngDownloadLink";
import { mediaUrl } from "@/lib/academy";
import { FILE_META, SECTION_META, downloadHref } from "@/lib/marketing";
import { MarketingAssetDialog } from "@/components/admin/MarketingAssetDialog";

const FileActions = ({ a, onPersonalize }) => {
  if (!a.file_url) return null;
  const canva = a.file_type === "canva";
  return (
    <>
      <Button size="sm" variant="ghost" asChild data-testid={`marketing-admin-view-${a.id}`}>
        <a href={canva ? a.file_url : mediaUrl(a.file_url)} target="_blank" rel="noreferrer"><Eye className="w-4 h-4 mr-1" />View</a>
      </Button>
      {!canva && (
        <Button size="sm" variant="ghost" asChild data-testid={`marketing-admin-download-${a.id}`}>
          <a href={downloadHref(a)} download={a.file_name || true}><Download className="w-4 h-4 mr-1" />Download</a>
        </Button>
      )}
      <PngDownloadLink asset={a} label="PNG" className="px-2" />
      {a.personalize && a.is_active && <Button size="sm" onClick={() => onPersonalize(a)} data-testid={`marketing-admin-personalize-${a.id}`}><Wand2 className="w-4 h-4 mr-1" />Personalize</Button>}
    </>
  );
};

const AssetRow = ({ a, i, last, onMove, onEdit, onActive, onUse, onPersonalize }) => {
  const Icon = FILE_META[a.file_type]?.icon || SECTION_META[a.category]?.icon;
  return (
    <div className={`p-3 flex flex-wrap items-center gap-3 ${a.is_active ? "" : "opacity-60"}`} data-testid={`marketing-admin-row-${a.id}`}>
      <div className="flex flex-col">
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`marketing-move-up-${a.id}`}><ArrowUp className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={last} onClick={() => onMove(i, 1)} data-testid={`marketing-move-down-${a.id}`}><ArrowDown className="w-4 h-4" /></Button>
      </div>
      <div className={`w-20 aspect-video rounded-md overflow-hidden bg-gradient-to-br ${SECTION_META[a.category]?.tint || ""} flex items-center justify-center shrink-0`}>
        {a.thumbnail_url ? <img src={mediaUrl(a.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : Icon && <Icon className="w-6 h-6" />}
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="font-medium">{a.title}</div>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
          <span>{FILE_META[a.file_type]?.label || "—"}</span>
          <span className="break-all" data-testid={`marketing-admin-file-state-${a.id}`}>{a.file_url ? (a.file_type === "canva" ? "Canva link set" : `File: ${a.file_name || "uploaded"}`) : "Coming soon"}</span>
          {a.personalize && <span className="text-primary" data-testid={`marketing-admin-personalized-${a.id}`}>Personalized</span>}
        </div>
      </div>
      {!a.is_active && <Badge variant="outline" data-testid={`marketing-archived-badge-${a.id}`}>Archived</Badge>}
      <FileActions a={a} onPersonalize={onPersonalize} />
      {a.email_body && <Button size="sm" onClick={() => onUse(a)} data-testid={`marketing-admin-use-${a.id}`}><Mail className="w-4 h-4 mr-1" />Use / edit my copy</Button>}
      <Button size="sm" variant="outline" onClick={() => onEdit(a)} data-testid={`marketing-edit-${a.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
      <Button size="sm" variant="ghost" onClick={() => onActive(a, !a.is_active)} data-testid={`marketing-archive-${a.id}`}>
        {a.is_active ? <><Archive className="w-4 h-4 mr-1" />Archive</> : <><ArchiveRestore className="w-4 h-4 mr-1" />Restore</>}
      </Button>
    </div>
  );
};

export default function MarketingAdminPanel() {
  const [sections, setSections] = useState(null);
  const [edit, setEdit] = useState(null);
  const [using, setUsing] = useState(null);
  const [personalizing, setPersonalizing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/marketing/assets"); setSections(data.sections); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const move = async (si, i, dir) => {
    const list = [...sections[si].assets];
    [list[i], list[i + dir]] = [list[i + dir], list[i]];
    setSections((s) => s.map((x, k) => (k === si ? { ...x, assets: list } : x)));
    try { await api.post("/admin/marketing/reorder", { ids: list.map((a) => a.id) }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const setActive = async (a, is_active) => {
    try { await api.post(`/admin/marketing/assets/${a.id}/active`, { is_active }); toast.success(is_active ? "Resource restored" : "Resource archived"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!sections) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="marketing-admin-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">Marketing Assets</h2>
          <p className="text-sm text-muted-foreground">Upload files or add Canva links, set the order partners see in each section, and archive old resources.</p>
        </div>
        <Button onClick={() => setEdit({})} data-testid="marketing-new-asset-btn"><Plus className="w-4 h-4 mr-1" />New resource</Button>
      </div>
      {sections.map((s, si) => (
        <div key={s.title} className="space-y-2" data-testid={`marketing-admin-section-${si}`}>
          <div className="text-sm font-semibold">{s.title} <span className="text-xs text-muted-foreground font-mono">{s.assets.length}</span></div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {s.assets.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No resources.</div> : s.assets.map((a, i) => (
              <AssetRow key={a.id} a={a} i={i} last={i === s.assets.length - 1} onMove={(x, d) => move(si, x, d)} onEdit={setEdit} onActive={setActive} onUse={setUsing} onPersonalize={setPersonalizing} />
            ))}
          </div>
        </div>
      ))}
      <EmailTemplateDialog asset={using} onClose={() => setUsing(null)} />
      <PersonalizeDialog asset={personalizing} onClose={() => setPersonalizing(null)} />
      <MarketingAssetDialog asset={edit?.id ? edit : null} categories={sections.map((s) => s.title)} open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
